import { useState, useEffect } from "react";
import i18n from "i18n";

import { Feature } from "../redux/services/config";

import { parseLanguageObject } from "./../utils/parseLanguageObject";
import { regexify } from "../utils/regexifySearchTerm";
import { normalize } from "../utils/normalizeString";

interface Props {
  features: Feature[];
  searchTerm: string;
  onSearchInputActive: (enabled: boolean) => void;
  offlineSearchEnabled?: boolean;
}

export interface SearchResultStore {
  [featureTitle: string]: Feature[];
}

export const MIN_CHARS = 2;

// Features with these subcategories shouldn't appear in search results (or suggestions).
// At the time of writing, these subcategories reflect a subset of "circulation" category features,
// i.e. features relating to ways of moving around a space
const BLACKLISTED_FEATURE_SUBCATEGORIES = [
  "lift",
  "stairs",
  "escalator",
  "ramp",
  "steps",
];

function isFeatureSearchable(feature: Feature): boolean {
  return (
    !feature.properties.is_temporarily_closed &&
    (!feature.properties.subcategory ||
      !BLACKLISTED_FEATURE_SUBCATEGORIES.includes(
        feature.properties.subcategory,
      ))
  );
}

export const useSearchResults = ({
  features,
  searchTerm,
  onSearchInputActive,
  offlineSearchEnabled = true,
}: Props) => {
  // Store the search results in an object where the key is the feature name which holds an array of features associated with the same name, so we can return all of them on result click
  const [resultStore, setResultStore] = useState<SearchResultStore>({});
  const [isTitleSearch, setIsTitleSearch] = useState(false);

  const normalizedSearchTerm = searchTerm.trim();

  // Effect to handle search logic
  useEffect(() => {
    setResultStore({});
    setIsTitleSearch(false);

    if (!offlineSearchEnabled) return;

    if (normalizedSearchTerm.length >= MIN_CHARS) {
      // Reset result store if there are two spaces in a row
      if (/ {2}/.test(searchTerm)) {
        return;
      }

      const nextResultStore: SearchResultStore = {};
      let matchedOnTitle = false;

      const matchesSearchTerm = (value?: string | null) => {
        if (!value) return false;

        const normalizedValue = normalize(String(value).toLowerCase());

        return regexify(normalizedSearchTerm).test(normalizedValue);
      };

      features.forEach((feature) => {
        if (!isFeatureSearchable(feature)) return;

        const popupHeader = parseLanguageObject(
          feature.properties.popup_header ?? [],
          i18n.language,
        );
        const name = parseLanguageObject(
          feature.properties.name ?? [],
          i18n.language,
        );

        const hasMatch = [popupHeader, name].some(matchesSearchTerm);

        const resultKey = popupHeader || name;

        if (!hasMatch || !resultKey) return;

        matchedOnTitle =
          matchedOnTitle ||
          matchesSearchTerm(popupHeader) ||
          matchesSearchTerm(name);

        nextResultStore[resultKey] = [
          ...(nextResultStore[resultKey] || []),
          feature,
        ];
      });

      setResultStore(nextResultStore);
      setIsTitleSearch(matchedOnTitle);
    }
  }, [features, offlineSearchEnabled, normalizedSearchTerm, searchTerm]);

  // Effect to handle search input active state
  useEffect(() => {
    onSearchInputActive(true);
  }, [onSearchInputActive]);

  // Effect to clear results if the search term is too short
  useEffect(() => {
    if (searchTerm.length < MIN_CHARS) {
      setResultStore({});
    }
  }, [searchTerm.length]);

  return {
    results: resultStore,
    isTitleSearch,
    debouncedSearchTerm: normalizedSearchTerm,
    isFetching: false,
    isOfflineSearchEnabled: offlineSearchEnabled,
  };
};
